/**
 * Centralized validation functions based on paymentValidation.ts logic
 * These functions provide consistent validation across all form schemas
 */

export {
  BANKING_FIELD_PLACEHOLDERS,
  formatBicSwift,
  formatIban,
  formatSortCode,
  isValidAccountNumber,
  isValidBicSwift,
  isValidIban,
  isValidSortCode,
  normalizeAccountNumber,
  normalizeBankingFormData,
  normalizeBicSwift,
  normalizeIban,
  normalizeSortCode,
} from './bankingValidation';

export {
  validateAccountNumber,
  validateBicSwift,
  validateIban,
  validateInternationalBanking,
  validateSortCode,
} from './bankingValidation';

// Basic required field validation
export const validateRequired = (
  value: unknown,
  fieldName: string
): string | undefined => {
  if (!value || String(value).trim() === '') {
    return `${fieldName} is required`;
  }
  return undefined;
};

// Amount validation with positive number check
export const validateAmount = (value: unknown): string | undefined => {
  if (!value) {
    return 'Amount is required';
  }
  const numValue = Number(value);
  if (isNaN(numValue) || numValue <= 0) {
    return 'Amount must be a positive number';
  }
  return undefined;
};

// Currency validation with custom currency support
export const validateCurrency = (
  value: unknown,
  customValue?: unknown
): string | undefined => {
  if (!value || String(value).trim() === '') {
    return 'Currency is required';
  }

  if (value === 'other') {
    if (!customValue || String(customValue).trim().length < 2) {
      return 'Currency code/name must be at least 2 characters';
    }
  }

  return undefined;
};

// Duplicate reference validation (simplified for schema use)
export const validateUniqueReference = (
  value: unknown,
  existingData?: unknown[]
): string | undefined => {
  if (!value || String(value).trim() === '') {
    // ;<div className="" />
    return undefined; // Let required validation handle empty values
  }

  if (!existingData || !Array.isArray(existingData)) {
    return undefined;
  }

  const newReference = String(value).trim();
  const duplicateExists = existingData.some((row: unknown) => {
    if (!row || typeof row !== 'object') return false;
    const rowObj = row as Record<string, unknown>;
    const hasData =
      rowObj.payee || rowObj.amount || rowObj.purpose || rowObj.reference;
    if (!hasData) return false;
    return String(rowObj.reference || '').trim() === newReference;
  });

  if (duplicateExists) {
    return 'Payment reference must be unique';
  }

  return undefined;
};

// Tag picker validation for required fields
export const validateTagPicker = (
  value: unknown,
  fieldName: string
): string | undefined => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return `${fieldName} is required`;
  }
  return undefined;
};

// Checkbox validation for required fields (at least one option must be selected)
export const validateCheckbox = (
  value: unknown,
  fieldName: string
): string | undefined => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return `At least one ${fieldName} option must be selected`;
  }
  return undefined;
};

